/** @jsxImportSource @emotion/react */

import React, { useMemo } from 'react'
import { Form } from 'react-final-form'
import createDecorator from 'final-form-focus'
import { Button, Modal, Typography } from '@mui/material'
import { InputField } from 'components/Input'
import { SwitchField } from 'components/Switch'
import { findAllExtensionExams_findAllExtensionExam_items as Exam } from '__generatedTypes__/findAllExtensionExams'
import * as classes from './styles'

const focusOnErrors = createDecorator<Exam, Partial<Exam>>()

const validate = (values: Exam) =>
  !values.showExam && !values.message?.trim()
    ? { message: 'Message is required when the Extension Interface is hidden' }
    : {}

export type ExamAvailabilityModalProps = {
  isOpen: boolean
  exam: Exam | null
  onSubmit: (exam: Exam) => void
  onClose: () => void
}

export const ExamAvailabilityModal = ({ isOpen, exam, onSubmit, onClose }: ExamAvailabilityModalProps) => {
  const initialValues: Exam = useMemo(() => ({ ...(exam as Exam) }), [exam])

  return (
    <Modal open={isOpen} onClose={onClose}>
      <div css={classes.modalContainerWrapper} onClick={onClose}>
        <div css={classes.modalContainer} onClick={(e) => e.stopPropagation()}>
          <Form
            decorators={[focusOnErrors]}
            validate={validate}
            onSubmit={onSubmit}
            initialValues={initialValues}
            render={({ handleSubmit, submitting, values }) => (
              <form css={classes.modalForm} onSubmit={handleSubmit} data-testid="exam-availability-modal">
                <Typography css={classes.modalTitle} variant="title2">
                  {exam?.title}
                </Typography>

                <SwitchField name="showExam" label="Show Extension Interface to students" css={classes.switchLabel} />

                {!values.showExam && (
                  <div css={classes.infoBox}>
                    <Typography variant="body2" css={classes.infoBoxText}>
                      The Extension Interface will be hidden for this exam. Students will see the message below instead.
                    </Typography>
                  </div>
                )}

                <InputField
                  name="message"
                  label="Message"
                  placeholder="e.g. This exam was recently updated. We're working on a fix — check back soon!"
                  multiline
                  fullWidth
                />

                <div css={classes.buttons}>
                  <Button
                    variant="outlined"
                    color="blue"
                    size="large"
                    disabled={submitting}
                    onClick={onClose}
                    fullWidth
                  >
                    Cancel
                  </Button>
                  <Button variant="contained" type="submit" color="blue" size="large" disabled={submitting} fullWidth>
                    Save
                  </Button>
                </div>
              </form>
            )}
          />
        </div>
      </div>
    </Modal>
  )
}
